import { Badge, Grid, Table, Text } from "@mantine/core";
import dayjs from "dayjs";
import ReactApexChart from "react-apexcharts";
import useSWR from "swr";
import { useRef, useState } from "react";
import AdminKpiCard from "../AdminKpiCard/AdminKpiCard";
import classes from "./AdminDashboardView.module.css";
import { buildDashboardPlansQueryString, dashboardSwrKey } from "../../utils/dashboardQueryKeys";
import { DASHBOARD_EMPTY_STATE } from "../../constants";
import { fetcher } from "../../../../services/fetcher";

const FONT = "Inter, -apple-system, BlinkMacSystemFont, sans-serif";
const chartBase = {
  chart: { toolbar: { show: false }, animations: { enabled: false }, fontFamily: FONT },
  grid: { borderColor: "#eef0f4", strokeDashArray: 4, padding: { left: 2, right: 8 } },
  dataLabels: { enabled: false },
  tooltip: { theme: "light", style: { fontSize: "12px", fontFamily: FONT } },
  legend: { fontSize: "12px", fontFamily: FONT },
};

const truncate = (s, n = 20) => { const str = String(s ?? ""); return str.length > n ? `${str.substring(0, n)}…` : str; };
const typeBadgeClass = (type) => {
  const t = String(type).toLowerCase();
  if (t.includes("mother")) return classes.badgeInfo;
  if (t.includes("master")) return classes.badgeSuccess;
  return classes.badgePending;
};

const AdminPlansTab = ({ filters, scope = "both", planType = "all" }) => {
  const [showAllExpiring, setShowAllExpiring] = useState(false);
  const expiringSectionRef = useRef(null);

  const plansCanon = buildDashboardPlansQueryString({ scope, filters, planType });
  const activePlansUrl = dashboardSwrKey("dashboard/active-plans-dashboard/", plansCanon);
  const expiringPlansUrl = dashboardSwrKey("dashboard/plans-expiring-soon/", plansCanon);
  const planPopularityUrl = dashboardSwrKey("dashboard/plan-popularity/", plansCanon);

  const toRows = (payload) =>
    Array.isArray(payload) ? payload
      : Array.isArray(payload?.results) ? payload.results
      : Array.isArray(payload?.data) ? payload.data
      : Array.isArray(payload?.items) ? payload.items
      : [];

  const { data: activePlansData } = useSWR(activePlansUrl, fetcher);
  const { data: expiringPlansData } = useSWR(expiringPlansUrl, fetcher);
  const { data: planPopularityData } = useSWR(planPopularityUrl, fetcher);

  const metricsSource =
    activePlansData?.metrics ||
    activePlansData?.kpis ||
    activePlansData?.data?.metrics ||
    activePlansData?.data ||
    activePlansData ||
    {};

  const readMetric = (key) =>
    metricsSource?.[key]?.current ??
    metricsSource?.[key]?.value ??
    metricsSource?.[key];

  const formatMetricCell = (v) =>
    v !== undefined && v !== null && v !== ""
      ? String(Number(v))
      : "—";

  const expiringRowsRaw =
    Array.isArray(expiringPlansData?.plans) ? expiringPlansData.plans
      : Array.isArray(expiringPlansData?.expiring_plans) ? expiringPlansData.expiring_plans
      : toRows(expiringPlansData);

  const computeDaysLeft = (dateValue) => {
    if (!dateValue) return 0;
    const target = dayjs(dateValue);
    if (!target.isValid()) return 0;
    const diff = target.startOf("day").diff(dayjs().startOf("day"), "day");
    return diff > 0 ? diff : 0;
  };

  const expiringSoonCount = expiringRowsRaw.filter((item) => {
    const apiDays = Number(item?.days_left_until_expiry ?? item?.days_left ?? item?.daysLeft);
    const daysLeft = Number.isFinite(apiDays)
      ? apiDays
      : computeDaysLeft(item?.deactivation_date || item?.expiry_date || item?.date);
    return daysLeft > 0 && daysLeft <= 30;
  }).length;

  const expiredPlansCount =
    expiringPlansData?.expired_count ?? expiringPlansData?.plans_expired;

  const dynamicPlanKpis = [
    {
      label: "Total Plans",
      value: formatMetricCell(readMetric("active_customized_plans")),
      tone: "blue",
    },
    {
      label: "Plans Expiring Soon",
      value: formatMetricCell(expiringSoonCount),
      tone: "green",
    },
    {
      label: "Plans Expired",
      value: formatMetricCell(expiredPlansCount),
      tone: "amber",
    },
  ];

  const popularityRows =
    Array.isArray(planPopularityData?.plans) ? planPopularityData.plans
      : Array.isArray(planPopularityData?.popularity) ? planPopularityData.popularity
      : Array.isArray(planPopularityData?.plan_popularity) ? planPopularityData.plan_popularity
      : toRows(planPopularityData);
  const topPlanCategories = popularityRows.slice(0, 8).map((item) => item?.plan_name || item?.name || "Plan");
  const hasPopularityPercentage = popularityRows.some((item) => item?.percentage != null);
  const topPlanValues = popularityRows.slice(0, 8).map((item) =>
    Number(item?.percentage ?? item?.contribution_value ?? item?.value ?? 0)
  );

  // No mock fallback — show empty state if API returns nothing
  const expiringRows = expiringRowsRaw.map((item) => ({
    name: item?.plan_name || item?.name || "—",
    type: item?.plan_type || item?.type || "—",
    operator: item?.operator_name || item?.operator || item?.partner_name || item?.region || "—",
    date: item?.deactivation_date || item?.expiry_date || item?.date || "—",
    daysLeft: (() => {
      const apiDays = Number(item?.days_left ?? item?.daysLeft);
      if (Number.isFinite(apiDays) && apiDays > 0) return apiDays;
      return computeDaysLeft(item?.deactivation_date || item?.expiry_date || item?.date);
    })(),
  }));
  const visibleExpiringRows = showAllExpiring ? expiringRows : expiringRows.slice(0, 5);
  const atRiskCount = expiringRows.filter((item) => Number(item.daysLeft) <= 14).length;

  return (
    <div className={classes.tabStack}>
      {/* KPIs */}
      <Grid gutter={12}>
        {dynamicPlanKpis.map((item) => (
          <Grid.Col span={{ base: 12, md: 4 }} key={item.label}>
            <div
              onClick={() => {
                if (item.label !== "Plans Expiring Soon") return;
                expiringSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className={item.label === "Plans Expiring Soon" ? classes.kpiNavCard : undefined}
            >
              <AdminKpiCard {...item} compact />
            </div>
          </Grid.Col>
        ))}
      </Grid>

      {/* Top Plans horizontal bar */}
      <Grid gutter={12}>
        <Grid.Col span={{ base: 12, md: 12 }}>
          <div className={classes.card}>
            <div className={classes.cardHeader}>Top Plans by Popularity</div>
            {/* Horizontal bar: plan names are long — avoids rotated/truncated x-axis mess */}
            {topPlanValues.length ? (
              <ReactApexChart
                options={{
                  ...chartBase,
                  chart: { ...chartBase.chart, type: "bar" },
                  plotOptions: {
                    bar: {
                      horizontal: true,
                      borderRadius: 4,
                      barHeight: "60%",
                      dataLabels: { position: "center" },
                    },
                  },
                  dataLabels: {
                    enabled: true,
                    style: { fontSize: "10px", fontWeight: 700, colors: ["#ffffff"] },
                    formatter: (v) =>
                      hasPopularityPercentage ? `${Number(v).toFixed(2)}%` : Number(v).toLocaleString("en-IN"),
                  },
                  colors: ["#2563eb"],
                  xaxis: {
                    categories: topPlanCategories,
                    tickAmount: 4,
                    labels: { show: false },
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                  },
                  yaxis: {
                    labels: {
                      formatter: (v) => truncate(v, 24),
                      style: { fontSize: "10px", fontFamily: FONT },
                    },
                  },
                  tooltip: {
                    y: {
                      formatter: (v) =>
                        hasPopularityPercentage
                          ? `${Number(v).toFixed(2)}%`
                          : Number(v).toLocaleString("en-IN"),
                    },
                  },
                }}
                series={[{ name: hasPopularityPercentage ? "Popularity %" : "Popularity", data: topPlanValues }]}
                type="bar"
                height={280}
              />
            ) : (
              <div className={classes.emptyState}>{DASHBOARD_EMPTY_STATE}</div>
            )}
          </div>
        </Grid.Col>
      </Grid>

      {/* Plans Expiring Soon */}
      <div className={classes.card} ref={expiringSectionRef}>
        <div className={classes.insightHeader}>
          <Text fw={600} c="#b91c1c" fz={13}>
            Plans expiring soon — critical insight
          </Text>
          <Badge className={classes.badgeFailed}>{atRiskCount} at risk</Badge>
        </div>
        {expiringRows.length ? (
          <>
            <Table className={classes.table} horizontalSpacing="sm" verticalSpacing={7}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Plan Name</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Operator</Table.Th>
                  <Table.Th>Deactivation Date</Table.Th>
                  <Table.Th>Days Left</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {visibleExpiringRows.map((item) => (
                  <Table.Tr
                    key={`${item.name}-${item.operator}`}
                    className={item.daysLeft <= 7 ? classes.rowCritical : item.daysLeft <= 14 ? classes.rowWarning : ""}
                  >
                    <Table.Td>{item.name}</Table.Td>
                    <Table.Td><Badge className={typeBadgeClass(item.type)}>{item.type}</Badge></Table.Td>
                    <Table.Td>{item.operator}</Table.Td>
                    <Table.Td>{item.date}</Table.Td>
                    <Table.Td>{item.daysLeft}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {expiringRows.length > 5 && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                <Text
                  size="sm"
                  c="blue"
                  style={{ cursor: "pointer", fontWeight: 600 }}
                  onClick={() => setShowAllExpiring((prev) => !prev)}
                >
                  {showAllExpiring ? "View Less" : "View More"}
                </Text>
              </div>
            )}
          </>
        ) : (
          <div className={classes.emptyState}>{DASHBOARD_EMPTY_STATE}</div>
        )}
      </div>

    </div>
  );
};

export default AdminPlansTab;
